import React from 'react';

const Section = ({ n, title, children }) => (
  <section className="mb-8" id={`p-${n}`}>
    <h2 className="font-comfortaa font-bold text-xl sm:text-2xl text-ink mt-8 mb-3">
      <span className="text-lime mr-2">{n}.</span>{title}
    </h2>
    <div className="space-y-3 text-ink-700 text-sm sm:text-base font-light leading-relaxed">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  const today = new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div className="bg-white min-h-screen font-mont">
      <header className="bg-black text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-comfortaa font-bold tracking-[0.25em] text-lime">PRIVACIDAD</div>
          <h1 className="mt-3 font-comfortaa font-bold text-3xl sm:text-5xl">Aviso de Privacidad</h1>
          <p className="mt-3 text-white/60 font-light text-sm">
            Última actualización: {today} · Versión 1.0 · Borrador para revisión legal
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="privacy-page">
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-8 text-sm text-amber-900 font-light">
          ⚠️ <strong className="font-medium">Borrador.</strong> Aviso de privacidad integral preparado conforme a la Ley
          Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y su Reglamento. Sujeto a
          revisión por un especialista en privacidad antes de publicación.
        </div>

        <p className="text-ink-700 text-sm sm:text-base font-light leading-relaxed">
          <strong className="font-medium">Mobility Car Rental, S.A. de C.V.</strong> ("Mobility"), con domicilio en México,
          en su carácter de <em>responsable del tratamiento de datos personales</em>, hace de tu conocimiento el presente
          Aviso de Privacidad Integral.
        </p>

        <Section n="1" title="Datos personales que recabamos">
          <p>
            Para prestar nuestros servicios recabamos las siguientes categorías de datos personales, según corresponda al rol del Titular:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong className="font-medium">Identificación:</strong> nombre completo, fecha de nacimiento, INE/identificación oficial, fotografías.</li>
            <li><strong className="font-medium">Contacto:</strong> correo electrónico, teléfono, domicilio.</li>
            <li><strong className="font-medium">Documentación de manejo (Renters):</strong> licencia de conducir vigente.</li>
            <li><strong className="font-medium">Documentación vehicular (Mobilitiers):</strong> tarjeta de circulación, póliza de seguro, fotografías del vehículo.</li>
            <li><strong className="font-medium">Financieros:</strong> tokens de método de pago a través de Stripe (Mobility no almacena el número completo de tarjeta).</li>
            <li><strong className="font-medium">Geolocalización:</strong> ubicación aproximada al usar la búsqueda y mapa.</li>
            <li><strong className="font-medium">Uso del servicio:</strong> historial de reservas, reseñas, mensajes, fotografías de check-in/out, métricas de uso de la app.</li>
          </ul>
          <p>No recabamos datos sensibles en términos del artículo 3, fracción VI, de la LFPDPPP.</p>
        </Section>

        <Section n="2" title="Finalidades del tratamiento">
          <p><strong className="font-medium">Finalidades primarias</strong> (necesarias para la relación con el Titular):</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Crear y mantener tu cuenta, así como validar tu identidad.</li>
            <li>Procesar reservas, pagos, depósitos de seguridad, reembolsos y comisiones.</li>
            <li>Coordinar el flujo de check-in/out y conservar evidencia fotográfica.</li>
            <li>Atención al cliente, soporte y comunicación transaccional (notificaciones, correos).</li>
            <li>Cumplir obligaciones legales, fiscales y regulatorias.</li>
            <li>Prevenir fraude y actividades ilícitas.</li>
          </ul>
          <p><strong className="font-medium">Finalidades secundarias</strong> (no necesarias, requieren tu consentimiento expreso):</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Mercadotecnia, publicidad y prospección comercial (correo, push, WhatsApp opcional).</li>
            <li>Análisis estadístico y elaboración de perfiles de comportamiento para mejorar la oferta.</li>
            <li>Programa de referidos y promociones de socios comerciales.</li>
          </ul>
          <p>Puedes oponerte a las finalidades secundarias en cualquier momento (ver Sección 6).</p>
        </Section>

        <Section n="3" title="Transferencias de datos">
          <p>Para operar la Plataforma transferimos datos a los siguientes encargados/terceros, sólo para las finalidades indicadas:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong className="font-medium">Stripe, Inc.</strong> (Estados Unidos) — procesamiento de pagos, depósitos y onboarding de Mobilitiers a través de Stripe Connect Express.</li>
            <li><strong className="font-medium">Cloudinary</strong> (Estados Unidos) — almacenamiento y entrega de imágenes (fotos de autos, check-in/out, perfil).</li>
            <li><strong className="font-medium">Resend</strong> (Estados Unidos) — envío de correos transaccionales.</li>
            <li><strong className="font-medium">Google LLC</strong> (Estados Unidos) — Maps, Places y Geocoding API para localizar vehículos y direcciones.</li>
            <li><strong className="font-medium">MongoDB Atlas (MongoDB, Inc.)</strong> — almacenamiento de la base de datos.</li>
            <li><strong className="font-medium">Autoridades competentes</strong> — cuando exista requerimiento fundado y motivado (Profeco, Fiscalía, autoridad fiscal, etc.).</li>
          </ul>
          <p>
            Estas transferencias se realizan al amparo de los supuestos del artículo 37 de la LFPDPPP (necesarias para la
            relación jurídica) y bajo cláusulas contractuales de protección con cada encargado.
          </p>
        </Section>

        <Section n="4" title="Plazo de conservación">
          <p>
            Los datos se conservan durante la vigencia de la cuenta y, posteriormente, durante los plazos legales aplicables
            (típicamente 5 años por obligaciones fiscales y comerciales). Las fotografías de check-in/out se conservan por al
            menos 24 meses para resolución de eventuales reclamaciones.
          </p>
        </Section>

        <Section n="5" title="Medidas de seguridad">
          <p>
            Implementamos medidas administrativas, físicas y técnicas razonables para proteger tus datos contra pérdida,
            alteración o acceso no autorizado, incluyendo cifrado en tránsito (TLS), cifrado en reposo (Atlas), control de
            accesos por roles, hash bcrypt para contraseñas, tokenización de pagos por Stripe y monitoreo de actividad.
          </p>
        </Section>

        <Section n="6" title="Derechos ARCO y revocación del consentimiento">
          <p>
            Tienes derecho a <strong className="font-medium">Acceder, Rectificar, Cancelar u Oponerte</strong> al tratamiento de
            tus datos (Derechos ARCO), así como a <strong className="font-medium">revocar el consentimiento</strong> que hayas
            otorgado y a <strong className="font-medium">limitar el uso o divulgación</strong> de los mismos.
          </p>
          <p>
            Para ejercerlos, envía una solicitud a <a href="mailto:privacidad@mobility-car-rental.com" className="text-ink underline">privacidad@mobility-car-rental.com</a>
            indicando: (i) nombre y medio para recibir respuesta; (ii) copia de identificación oficial; (iii) descripción clara
            del derecho que ejerces; (iv) los datos sobre los que recae. Responderemos en un plazo no mayor a 20 días hábiles
            conforme a la LFPDPPP.
          </p>
          <p>También puedes inscribirte en el <strong className="font-medium">Registro Público de Consumidores</strong> de Profeco para limitar publicidad.</p>
        </Section>

        <Section n="7" title="Uso de cookies y tecnologías similares">
          <p>
            La Plataforma utiliza cookies estrictamente necesarias para la sesión y, opcionalmente, cookies analíticas para medir
            uso (estas últimas pueden desactivarse desde la configuración del navegador). No utilizamos cookies de publicidad
            comportamental sin tu consentimiento.
          </p>
        </Section>

        <Section n="8" title="Datos de menores">
          <p>
            La Plataforma no está dirigida a menores de 18 años. No recabamos intencionalmente datos de personas menores de edad.
            Si detectamos un registro de un menor, procederemos a su cancelación.
          </p>
        </Section>

        <Section n="9" title="Cambios al Aviso">
          <p>
            Cualquier modificación a este Aviso será publicada en esta misma URL con la fecha de actualización. En caso de
            cambios sustanciales relacionados con finalidades primarias, te lo notificaremos por correo.
          </p>
        </Section>

        <Section n="10" title="Autoridad reguladora">
          <p>
            Si consideras que tu derecho a la protección de datos personales ha sido vulnerado, puedes presentar una denuncia
            ante el <strong className="font-medium">Instituto Nacional de Transparencia, Acceso a la Información y Protección
            de Datos Personales (INAI)</strong> en <a href="https://home.inai.org.mx" target="_blank" rel="noreferrer" className="text-ink underline">home.inai.org.mx</a>.
          </p>
        </Section>

        <p className="mt-12 text-xs text-ink-400 font-light">
          Documento sujeto a revisión legal · Conforme a LFPDPPP, su Reglamento y Lineamientos del INAI ·
          © Mobility Car Rental, S.A. de C.V.
        </p>
      </main>
    </div>
  );
};

export default Privacy;
